import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Key,
  Lock,
  Eye,
  EyeOff,
  Copy,
  Edit,
  Trash2,
  Server,
  Globe,
  Database,
  Wifi,
  Shield,
  FolderOpen,
  AlertTriangle,
  Clock,
  X,
  RefreshCw,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';

interface Credential {
  id: string;
  name: string;
  description?: string;
  credentialType: string;
  username?: string;
  password: string;
  url?: string;
  ipAddress?: string;
  port?: number;
  hostname?: string;
  notes?: string;
  tags?: string;
  accessLevel: string;
  expiresAt?: string;
  rotationDays?: number;
  lastRotatedAt?: string;
  lastAccessedAt?: string;
  createdAt: string;
  category?: {
    id: string;
    name: string;
    color?: string;
    icon?: string;
  };
}

interface CredentialCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  _count?: {
    credentials: number;
  };
}

interface Stats {
  total: number;
  byType: Record<string, number>;
  byCategory: Array<{ id: string; name: string; color?: string; count: number }>;
  expiringSoon: number;
  needsRotation: number;
  recentAccess: number;
}

const credentialTypeIcons: Record<string, any> = {
  LOGIN: Lock,
  API_KEY: Key,
  SSH_KEY: Server,
  DATABASE: Database,
  CERTIFICATE: Shield,
  WIFI: Wifi,
  VPN: Globe,
  OTHER: Key,
};

const credentialTypeLabels: Record<string, string> = {
  LOGIN: 'Login Credentials',
  API_KEY: 'API Key',
  SSH_KEY: 'SSH Key',
  DATABASE: 'Database',
  CERTIFICATE: 'Certificate',
  WIFI: 'WiFi',
  VPN: 'VPN',
  OTHER: 'Other',
};

const accessLevelColors: Record<string, string> = {
  ADMIN: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  MANAGER: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  TECHNICIAN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  RESTRICTED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

export default function PasswordVaultPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState<Credential | null>(null);
  const [viewingCredential, setViewingCredential] = useState<Credential | null>(null);
  const [showAccessLogs, setShowAccessLogs] = useState(false);

  // Fetch credentials
  const { data: credentialsData, isLoading } = useQuery({
    queryKey: ['credentials', { page, search, category: selectedCategory, type: selectedType }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCategory) params.append('categoryId', selectedCategory);
      if (selectedType) params.append('credentialType', selectedType);
      const response = await api.get(`/password-vault/credentials?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['credential-categories'],
    queryFn: async () => {
      const response = await api.get('/password-vault/categories');
      return response.data;
    },
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['credential-stats'],
    queryFn: async () => {
      const response = await api.get('/password-vault/stats');
      return response.data;
    },
  });

  const credentials: Credential[] = credentialsData?.data || [];
  const categories: CredentialCategory[] = categoriesData?.data || [];
  const stats: Stats = statsData?.data || { total: 0, byType: {}, byCategory: [], expiringSoon: 0, needsRotation: 0, recentAccess: 0 };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/password-vault/credentials/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['credential-stats'] });
      toast({ title: 'Credential deleted successfully' });
    },
  });

  const handleViewCredential = async (id: string) => {
    try {
      const response = await api.get(`/password-vault/credentials/${id}`);
      setViewingCredential(response.data.data);
    } catch (error: any) {
      toast({ title: error.response?.data?.error || 'Failed to load credential', variant: 'destructive' });
    }
  };

  const handleCopyToClipboard = async (text: string, field: string, credentialId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Log the copy action
      await api.post(`/password-vault/credentials/${credentialId}/copy`);
      toast({ title: `${field} copied to clipboard` });
    } catch (error) {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  const getCredentialTypeIcon = (type: string) => {
    const Icon = credentialTypeIcons[type] || Key;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Password Vault</h1>
          <p className="text-muted-foreground">Securely store and manage IT credentials</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCategoryModal(true)}>
            <FolderOpen className="w-4 h-4 mr-2" />
            Categories
          </Button>
          <Button variant="outline" onClick={() => setShowAccessLogs(true)}>
            <History className="w-4 h-4 mr-2" />
            Access Logs
          </Button>
          <PermissionGate permission="password_vault.create">
            <Button onClick={() => { setEditingCredential(null); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Credential
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Credentials</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-2xl font-bold">{stats.expiringSoon}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <RefreshCw className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Needs Rotation</p>
                <p className="text-2xl font-bold">{stats.needsRotation}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recent Access (24h)</p>
                <p className="text-2xl font-bold">{stats.recentAccess}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search credentials..."
                className="pl-10"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={selectedType}
              onChange={(e) => { setSelectedType(e.target.value); setPage(1); }}
            >
              <option value="">All Types</option>
              {Object.entries(credentialTypeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Credentials Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
        </div>
      ) : credentials.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Lock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No credentials found</p>
            <Button variant="link" onClick={() => setShowModal(true)}>
              Add your first credential
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {credentials.map((credential) => (
            <Card key={credential.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: credential.category?.color ? `${credential.category.color}20` : '#3B82F620' }}
                    >
                      {getCredentialTypeIcon(credential.credentialType)}
                    </div>
                    <div>
                      <h3 className="font-semibold">{credential.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {credentialTypeLabels[credential.credentialType] || credential.credentialType}
                      </p>
                    </div>
                  </div>
                  <span className={cn("px-2 py-1 text-xs font-medium rounded-full", accessLevelColors[credential.accessLevel])}>
                    {credential.accessLevel}
                  </span>
                </div>

                {credential.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{credential.description}</p>
                )}

                <div className="space-y-2 text-sm">
                  {credential.username && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Username:</span>
                      <span className="font-mono">{credential.username}</span>
                    </div>
                  )}
                  {credential.url && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">URL:</span>
                      <a href={credential.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[150px]">
                        {credential.url}
                      </a>
                    </div>
                  )}
                  {credential.hostname && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Host:</span>
                      <span className="font-mono">{credential.hostname}{credential.port ? `:${credential.port}` : ''}</span>
                    </div>
                  )}
                  {credential.category && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Category:</span>
                      <span className="px-2 py-0.5 text-xs rounded" style={{ backgroundColor: credential.category.color ? `${credential.category.color}30` : '#E5E7EB' }}>
                        {credential.category.name}
                      </span>
                    </div>
                  )}
                </div>

                {credential.expiresAt && (
                  <div className={cn(
                    "mt-3 px-2 py-1 text-xs rounded flex items-center gap-1",
                    new Date(credential.expiresAt) < new Date() 
                      ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      : new Date(credential.expiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                  )}>
                    <Clock className="w-3 h-3" />
                    {new Date(credential.expiresAt) < new Date() ? 'Expired' : `Expires ${formatDate(credential.expiresAt)}`}
                  </div>
                )}

                <div className="flex items-center justify-end gap-1 mt-4 pt-3 border-t">
                  <Button variant="ghost" size="sm" onClick={() => handleViewCredential(credential.id)}>
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <PermissionGate permission="password_vault.update">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingCredential(credential); setShowModal(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </PermissionGate>
                  <PermissionGate permission="password_vault.delete">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this credential?')) {
                          deleteMutation.mutate(credential.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </PermissionGate>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <CredentialModal
          credential={editingCredential}
          categories={categories}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setEditingCredential(null);
            queryClient.invalidateQueries({ queryKey: ['credentials'] });
            queryClient.invalidateQueries({ queryKey: ['credential-stats'] });
          }}
        />
      )}

      {/* View Credential Modal */}
      {viewingCredential && (
        <ViewCredentialModal
          credential={viewingCredential}
          onClose={() => setViewingCredential(null)}
          onCopy={handleCopyToClipboard}
        />
      )}

      {/* Categories Modal */}
      {showCategoryModal && (
        <CategoriesModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['credential-categories'] });
          }}
        />
      )}

      {/* Access Logs Modal */}
      {showAccessLogs && (
        <AccessLogsModal onClose={() => setShowAccessLogs(false)} />
      )}
    </div>
  );
}

// ==================== CREDENTIAL MODAL ====================

interface CredentialModalProps {
  credential: Credential | null;
  categories: CredentialCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

function CredentialModal({ credential, categories, onClose, onSuccess }: CredentialModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: credential?.name || '',
    description: credential?.description || '',
    credentialType: credential?.credentialType || 'LOGIN',
    username: credential?.username || '',
    password: '',
    url: credential?.url || '',
    ipAddress: credential?.ipAddress || '',
    port: credential?.port?.toString() || '',
    hostname: credential?.hostname || '',
    notes: credential?.notes || '',
    tags: credential?.tags || '',
    accessLevel: credential?.accessLevel || 'ADMIN',
    expiresAt: credential?.expiresAt?.split('T')[0] || '',
    rotationDays: credential?.rotationDays?.toString() || '',
    categoryId: credential?.category?.id || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || (!credential && !formData.password)) {
      toast({ title: 'Name and password are required', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        port: formData.port ? parseInt(formData.port) : null,
        rotationDays: formData.rotationDays ? parseInt(formData.rotationDays) : null,
      };
      
      if (credential) {
        // Don't send password if it's empty (not changed)
        if (!payload.password) delete (payload as any).password;
        await api.put(`/password-vault/credentials/${credential.id}`, payload);
        toast({ title: 'Credential updated successfully' });
      } else {
        await api.post('/password-vault/credentials', payload);
        toast({ title: 'Credential created successfully' });
      }
      onSuccess();
    } catch (error: any) {
      toast({ title: error.response?.data?.error || 'Failed to save credential', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{credential ? 'Edit Credential' : 'Add Credential'}</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Production Server Admin"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Type</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.credentialType}
                  onChange={(e) => setFormData({ ...formData, credentialType: e.target.value })}
                >
                  {Object.entries(credentialTypeLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Category</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                >
                  <option value="">No Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Username</label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="admin@example.com"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Password {!credential && '*'}</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required={!credential}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={credential ? '••••••••' : 'Enter password'}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {credential && <p className="text-xs text-muted-foreground mt-1">Leave blank to keep existing</p>}
              </div>
              
              <div className="col-span-2">
                <label className="text-sm font-medium">URL</label>
                <Input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Hostname / IP Address</label>
                <Input
                  value={formData.hostname || formData.ipAddress}
                  onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                  placeholder="192.168.1.1 or server.local"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Port</label>
                <Input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                  placeholder="22"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Access Level</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.accessLevel}
                  onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
                >
                  <option value="ADMIN">Admin Only</option>
                  <option value="MANAGER">Manager+</option>
                  <option value="TECHNICIAN">Technician+</option>
                  <option value="RESTRICTED">All Users</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Expiration Date</label>
                <Input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Rotation Reminder (days)</label>
                <Input
                  type="number"
                  value={formData.rotationDays}
                  onChange={(e) => setFormData({ ...formData, rotationDays: e.target.value })}
                  placeholder="90"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Tags</label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="production, critical, mysql"
                />
              </div>
              
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm min-h-[60px]"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What is this credential used for?"
                />
              </div>
              
              <div className="col-span-2">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm min-h-[60px]"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (credential ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== VIEW CREDENTIAL MODAL ====================

interface ViewCredentialModalProps {
  credential: Credential;
  onClose: () => void;
  onCopy: (text: string, field: string, credentialId: string) => void;
}

function ViewCredentialModal({ credential, onClose, onCopy }: ViewCredentialModalProps) {
  const [showPassword, setShowPassword] = useState(false);

  const FieldRow = ({ label, value, sensitive, field }: { label: string; value?: string | null; sensitive?: boolean; field?: string }) => {
    if (!value) return null;
    
    return (
      <div className="flex items-center justify-between py-2 border-b last:border-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <span className={cn("font-mono text-sm", sensitive && !showPassword && "blur-sm select-none")}>
            {sensitive && !showPassword ? '••••••••••••' : value}
          </span>
          {field && (
            <Button variant="ghost" size="icon" onClick={() => onCopy(value, field, credential.id)}>
              <Copy className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              {credentialTypeIcons[credential.credentialType] ? 
                <Lock className="w-5 h-5 text-primary" /> : 
                <Key className="w-5 h-5 text-primary" />
              }
            </div>
            <div>
              <CardTitle className="text-lg">{credential.name}</CardTitle>
              <p className="text-sm text-muted-foreground">{credentialTypeLabels[credential.credentialType]}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <span className="text-sm text-yellow-800 dark:text-yellow-200">Show sensitive data</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          </div>

          <div className="space-y-0">
            <FieldRow label="Username" value={credential.username} field="Username" />
            <FieldRow label="Password" value={credential.password} sensitive field="Password" />
            <FieldRow label="URL" value={credential.url} field="URL" />
            <FieldRow label="Hostname" value={credential.hostname} field="Hostname" />
            {credential.port && <FieldRow label="Port" value={credential.port.toString()} field="Port" />}
            <FieldRow label="IP Address" value={credential.ipAddress} field="IP Address" />
          </div>

          {credential.notes && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-1">Notes</p>
              <p className="text-sm text-muted-foreground">{credential.notes}</p>
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1">
            <p>Created: {formatDate(credential.createdAt)}</p>
            {credential.lastAccessedAt && <p>Last accessed: {formatDate(credential.lastAccessedAt)}</p>}
            {credential.lastRotatedAt && <p>Last rotated: {formatDate(credential.lastRotatedAt)}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== CATEGORIES MODAL ====================

interface CategoriesModalProps {
  categories: CredentialCategory[];
  onClose: () => void;
  onUpdate: () => void;
}

function CategoriesModal({ categories, onClose, onUpdate }: CategoriesModalProps) {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CredentialCategory | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#3B82F6' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/password-vault/categories/${editingCategory.id}`, formData);
        toast({ title: 'Category updated' });
      } else {
        await api.post('/password-vault/categories', formData);
        toast({ title: 'Category created' });
      }
      setShowForm(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', color: '#3B82F6' });
      onUpdate();
    } catch (error: any) {
      toast({ title: error.response?.data?.error || 'Failed to save category', variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this category?')) return;
    try {
      await api.delete(`/password-vault/categories/${id}`);
      toast({ title: 'Category deleted' });
      onUpdate();
    } catch (error: any) {
      toast({ title: error.response?.data?.error || 'Failed to delete category', variant: 'destructive' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Credential Categories</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showForm ? (
            <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-muted rounded-lg">
              <Input
                required
                placeholder="Category name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <Input
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  className="w-12 h-10 p-1"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
                <span className="text-sm text-muted-foreground">Color</span>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm">{editingCategory ? 'Update' : 'Create'}</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditingCategory(null); }}>Cancel</Button>
              </div>
            </form>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          )}

          <div className="space-y-2">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center justify-between p-3 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: cat.color || '#3B82F6' }} />
                  <div>
                    <p className="font-medium">{cat.name}</p>
                    <p className="text-xs text-muted-foreground">{cat._count?.credentials || 0} credentials</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEditingCategory(cat); setFormData({ name: cat.name, description: cat.description || '', color: cat.color || '#3B82F6' }); setShowForm(true); }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ACCESS LOGS MODAL ====================

function AccessLogsModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['credential-access-logs'],
    queryFn: async () => {
      const response = await api.get('/password-vault/access-logs?limit=50');
      return response.data;
    },
  });

  const logs = data?.data || [];

  const actionColors: Record<string, string> = {
    VIEW: 'bg-blue-100 text-blue-800',
    COPY: 'bg-yellow-100 text-yellow-800',
    UPDATE: 'bg-green-100 text-green-800',
    DELETE: 'bg-red-100 text-red-800',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between flex-shrink-0">
          <CardTitle>Access Logs</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No access logs found</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log: any) => (
                <div key={log.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <span className={cn("px-2 py-1 text-xs font-medium rounded", actionColors[log.action] || 'bg-gray-100 text-gray-800')}>
                      {log.action}
                    </span>
                    <div>
                      <p className="font-medium text-sm">{log.credential?.name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">by {log.userName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{formatDate(log.createdAt)}</p>
                    {log.ipAddress && <p className="text-xs text-muted-foreground">{log.ipAddress}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
